import { createReducer, on } from '@ngrx/store';
import { UserListState } from '../models/users.model';
import { UserListActions } from './users.actions';

/**
 * Feature key for user list state
 */
export const USER_LIST_FEATURE_KEY = 'userList';

/**
 * Initial user list state
 */
export const INITIAL_USER_LIST_STATE: UserListState = {
  users: [],
  rows: [],
  loading: false,
  error: null,
  total: 0,
  pageIndex: 0,
  pageSize: 10,
  search: '',
};

/**
 * User list reducer
 */
export const userListReducer = createReducer(
  INITIAL_USER_LIST_STATE,
  on(UserListActions.loadUserList, state => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserListActions.loadUserListSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    error: null,
    users: response.data || [],
    total: response.pagination?.total || 0,
  })),
  on(UserListActions.loadUserListFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(UserListActions.setUserListSearch, (state, { search }) => ({
    ...state,
    search,
    pageIndex: 0, // Reset to first page on search
  })),
  on(
    UserListActions.setUserListPagination,
    (state, { pageIndex, pageSize }) => ({
      ...state,
      pageIndex,
      pageSize,
    })
  ),
  on(UserListActions.setUserListSort, state => ({
    ...state,
    // Sort state can be stored here if needed
  })),
  on(UserListActions.clearUserListError, state => ({
    ...state,
    error: null,
  })),
  // Update user status optimistically when freeze/unfreeze/block/unblock succeed.
  //
  // The corresponding `*Success` effects immediately re-dispatch `loadUserList`,
  // so this optimistic write is short-lived; we use the server code as a
  // placeholder `translation` and let the next reload replace it with the
  // properly-localized label.
  on(UserListActions.freezeUserSuccess, (state, { userId }) => ({
    ...state,
    users: state.users.map(user =>
      user.id === userId
        ? { ...user, status: { value: 'FROZEN', translation: 'FROZEN' } }
        : user
    ),
  })),
  on(UserListActions.unfreezeUserSuccess, (state, { userId }) => ({
    ...state,
    users: state.users.map(user =>
      user.id === userId
        ? { ...user, status: { value: 'ACTIVE', translation: 'ACTIVE' } }
        : user
    ),
  })),
  on(UserListActions.blockUserSuccess, (state, { userId }) => ({
    ...state,
    users: state.users.map(user =>
      user.id === userId
        ? { ...user, status: { value: 'BLOCKED', translation: 'BLOCKED' } }
        : user
    ),
  })),
  on(UserListActions.unblockUserSuccess, (state, { userId }) => ({
    ...state,
    users: state.users.map(user =>
      user.id === userId
        ? { ...user, status: { value: 'ACTIVE', translation: 'ACTIVE' } }
        : user
    ),
  })),
  // The `updateUserRoleSuccess` effect immediately re-dispatches
  // `loadUserList`, so this optimistic write is short-lived; we use the server
  // code as a placeholder `translation` until the reload replaces it with the
  // properly-localized label.
  on(UserListActions.updateUserRoleSuccess, (state, { userId, role }) => ({
    ...state,
    users: state.users.map(user =>
      user.id === userId
        ? { ...user, role: { value: role, translation: role } }
        : user
    ),
  }))
);
